import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Heart } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", plan: null });

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState({ status: "error" });
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          if (data.kind !== "donation") await refresh();
          localStorage.setItem(
            "chatmilu.milu.celebrate",
            data.kind === "donation" ? "your donation" : `${data.plan_id} `.trim(),
          );
          return setState({ status: "paid", plan: data.plan_id, kind: data.kind, amount: data.amount });
        }
        if (["failed", "expired"].includes(data.payment_status)) return setState({ status: "failed" });
      } catch {
        return setState({ status: "error" });
      }
      if (++tries > 15) return setState({ status: "timeout" });
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="payment-success-page">
      {state.status === "checking" && (
        <>
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand" />
          <p className="mt-5 text-sm text-ink-soft">Confirming your payment with Stripe…</p>
        </>
      )}
      {state.status === "paid" && state.kind === "donation" && (
        <>
          <Heart className="mx-auto h-10 w-10 text-brand" />
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter" data-testid="donation-paid-title">
            Thank you{state.amount ? ` for the $${state.amount}` : ""}!
          </h1>
          <p className="mt-2 text-sm text-ink-soft">
            Your donation keeps the free tier free. Nothing about your plan changed — this was a pure gift.
          </p>
        </>
      )}
      {state.status === "paid" && state.kind !== "donation" && (
        <>
          <CheckCircle2 className="mx-auto h-10 w-10 text-forest" />
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter" data-testid="payment-paid-title">
            You're on {state.plan}
          </h1>
          <p className="mt-2 text-sm text-ink-soft">Your new limits and features are active right now.</p>
        </>
      )}
      {["failed", "timeout", "error"].includes(state.status) && (
        <>
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter">Payment not confirmed</h1>
          <p className="mt-2 text-sm text-ink-soft">
            We couldn't confirm the payment. Nothing has been charged twice — try again from the Plans page.
          </p>
        </>
      )}
      <div className="mt-8 flex justify-center gap-3">
        <Link to="/chat">
          <Button data-testid="payment-goto-chat" className="min-h-[48px] rounded-full bg-brand px-7 hover:bg-brand-hover">
            Back to chat
          </Button>
        </Link>
        <Link to="/pricing">
          <Button variant="outline" className="min-h-[48px] rounded-full px-7">Plans</Button>
        </Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle className="mx-auto h-10 w-10 text-ink-soft" />
      <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tighter">Checkout cancelled</h1>
      <p className="mt-2 text-sm text-ink-soft">No charge was made. You can upgrade whenever you're ready.</p>
      <Link to="/pricing">
        <Button className="mt-8 min-h-[48px] rounded-full bg-brand px-7 hover:bg-brand-hover">Back to plans</Button>
      </Link>
    </div>
  );
}
